import React, { useState } from "react";
import { Table, Button, Tag, Space } from "antd";
import CreateQRModal from "./CreateQRModal";
import EditQRModal from "./EditQRModal";
import DeleteQRModal from "./DeleteQRModal";
import { useAuth } from "../../../../context/authContext";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const QRList = ({ qrCodes = [], onCreate, onEdit, onDelete, loadingQR }) => {
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const { user } = useAuth();
  const { language } = useLanguage();
  const text = dashboardContent[language].qr.list;

  const handleShowViewModal = (qrCode) => {
    setSelectedQRCode(qrCode);
    setIsViewModalVisible(true);
  };

  const handleShowEditModal = (qrCode) => {
    setSelectedQRCode(qrCode);
    setIsEditModalVisible(true);
  };

  const handleShowDeleteModal = (qrCode) => {
    setSelectedQRCode(qrCode);
    setIsDeleteModalVisible(true);
  };

  const handleCloseModals = () => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setIsDeleteModalVisible(false);
    setIsViewModalVisible(false);
    setSelectedQRCode(null);
  };

  const columns = [
    {
      title: text.name,
      dataIndex: "name",
      render: (textValue, record) =>
        textValue ? (
          <a href="#a" onClick={() => handleShowViewModal(record)}>
            {textValue}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: text.form,
      dataIndex: "feedbackForm",
      render: (form, record) =>
        form?.name ? (
          <a
            href={
              record.data.startsWith("http")
                ? record.data
                : `https://${record.data}`
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {form.name}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: text.status,
      dataIndex: "status",
      render: (status) =>
        status ? (
          <Tag color={status === "Aktiv" ? "green" : "red"}>{status}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: text.createdAt,
      dataIndex: "createdAt",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString(
              language === "de" ? "de-DE" : "en-US"
            )
          : "-",
    },
    {
      title: text.actions,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => handleShowViewModal(record)}
            disabled={!record}
          >
            {text.show}
          </Button>
          <Button
            size="small"
            onClick={() => handleShowEditModal(record)}
            disabled={!record}
          >
            {text.edit}
          </Button>
          <Button
            danger
            size="small"
            onClick={() => handleShowDeleteModal(record)}
            disabled={!record}
          >
            {text.delete}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ overflowX: "auto" }}>
      <Table
        columns={columns}
        loading={loadingQR}
        dataSource={qrCodes}
        bordered
        title={() => text.tableTitle}
        footer={() =>
          text.tableFooter(qrCodes.length, user.reportLimits.qrCodeCreation)
        }
        rowKey={(record) => record._id}
        pagination={{ pageSize: 5 }}
        locale={{
          emptyText: text.emptyText,
        }}
      />

      <CreateQRModal
        visible={isCreateModalVisible}
        onClose={handleCloseModals}
        onCreate={onCreate}
      />

      <EditQRModal
        visible={isEditModalVisible}
        qrCode={selectedQRCode}
        onClose={handleCloseModals}
        onEdit={onEdit}
      />

      <DeleteQRModal
        visible={isDeleteModalVisible}
        qrCode={selectedQRCode}
        onClose={handleCloseModals}
        onDelete={onDelete}
      />

      {/* View QR Code Modal */}
      {isViewModalVisible && selectedQRCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-md p-6 rounded shadow-lg">
            <h3 className="text-lg font-semibold mb-4">{text.viewTitle}</h3>
            <div className="text-center">
              <img
                src={selectedQRCode.qrCodeImage}
                alt="QR Code"
                style={{ width: "80%", margin: "0 auto" }}
              />
              <p className="mt-4">
                <strong>{text.name}:</strong> {selectedQRCode.name}
              </p>
              <p>
                <strong>{text.form}:</strong>
                <a
                  href={
                    selectedQRCode.data.startsWith("http")
                      ? selectedQRCode.data
                      : `https://${selectedQRCode.data}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedQRCode.feedbackForm.name}
                </a>
              </p>
            </div>
            <div className="flex justify-end mt-4">
              <Button
                type="default"
                className="mr-2"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = selectedQRCode.qrCodeImage;
                  link.download = `${selectedQRCode.name}.png`;
                  link.click();
                }}
              >
                {text.download}
              </Button>
              <Button onClick={handleCloseModals}>{text.close}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRList;
